import '../styles/adminResponsive.css';
import { capacityText } from '../lib/capacity';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Link, Navigate } from 'react-router-dom';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [venue, setVenue] = useState(null);
  const [organizer, setOrganizer] = useState(null);
  const [activeRequest, setActiveRequest] = useState(null);
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    if (user?.role === 'organizer') return; // hooks stable; render kısmında redirect
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      if (user.role === 'admin') {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`);
        setStats(data);
      } else if (user.role === 'venue') {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/venues/me`);
        setVenue(data);
        const { data: m } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/matches`);
        setMatches(m);
      } else if (user.role === 'organizer') {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/organizers/me`);
        setOrganizer(data);
        const { data: m } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/matches`);
        setMatches(m);
        try {
          const { data: req } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/event-requests/me/active`);
          setActiveRequest(req);
        } catch (_) { /* ignore */ }
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Organizer artık search-first arayüz kullanır; Dashboard'a düşerse yönlendir.
  if (user?.role === 'organizer') {
    return <Navigate to="/organizer/home" replace />;
  }

  // ---------- ADMIN ----------
  if (user.role === 'admin') {
    return (
      <div className="p-8 max-w-7xl mx-auto admin-responsive-page">
        <div className="mb-8">
          <p className="text-sm text-[#1E3A8A] font-bold mb-1">Admin Paneli</p>
          <h1 className="text-3xl font-bold" data-testid="dashboard-title">Genel Bakış</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Toplam Mekân" value={stats?.total_venues || 0} testId="stat-total-venues" />
          <StatCard label="Bekleyen Mekân" value={stats?.pending_venues || 0} highlight testId="stat-pending-venues" />
          <StatCard label="Toplam Organizatör" value={stats?.total_organizers || 0} testId="stat-total-organizers" />
          <StatCard label="Bekleyen Organizatör" value={stats?.pending_organizers || 0} highlight testId="stat-pending-organizers" />
          <StatCard label="Toplam Eşleşme" value={stats?.total_matches || 0} testId="stat-total-matches" />
          <StatCard label="Bekleyen Eşleşme" value={stats?.pending_matches || 0} highlight testId="stat-pending-matches" />
          <StatCard label="Onaylı Eşleşme" value={stats?.approved_matches || 0} testId="stat-approved-matches" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <ActionCard
            emoji="🏢"
            title="Mekân Onayları"
            description={`${stats?.pending_venues || 0} mekân onay bekliyor`}
            to="/admin/venues"
            testId="dashboard-review-venues-button"
          />
          <ActionCard
            emoji="🎤"
            title="Organizatör Onayları"
            description={`${stats?.pending_organizers || 0} organizatör onay bekliyor`}
            to="/admin/organizers"
            testId="dashboard-review-organizers-button"
          />
        </div>
      </div>
    );
  }

  // ---------- VENUE / ORGANIZER ----------
  const profile = user.role === 'venue' ? venue : organizer;
  const roleLabel = user.role === 'venue' ? 'Mekân' : 'Organizatör';
  const profilePath = user.role === 'venue' ? '/venue/profile' : '/organizer/profile';
  const matchesPath = user.role === 'venue' ? '/venue/matches' : '/organizer/matches';

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <p className="text-sm text-[#1E3A8A] font-bold mb-1">{roleLabel} Paneli</p>
        <h1 className="text-3xl font-bold" data-testid="dashboard-title">Selam, {user.name} 👋</h1>
      </div>

      {!profile ? (
        // ============= NO PROFILE YET =============
        <div className="bg-[#1E3A8A]/5 border-2 border-[#1E3A8A]/30 rounded-2xl p-8 max-w-2xl">
          <div className="text-4xl mb-3">🚀</div>
          <h2 className="text-2xl font-bold mb-2">Hadi başlayalım!</h2>
          <p className="text-zinc-700 mb-6 leading-relaxed">
            Eşleşme önerileri için önce {roleLabel.toLowerCase()} profilini oluşturmalısın.
            <br />Sadece birkaç dakika sürer.
          </p>
          <Link
            to={profilePath}
            className="inline-block px-6 py-3 bg-[#1E3A8A] hover:bg-[#1E3A8A] text-white rounded-xl font-bold transition-colors shadow-sm"
            data-testid="dashboard-create-profile-button"
          >
            Profilimi Oluştur →
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Status row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border-2 border-zinc-200 rounded-2xl p-5">
              <p className="text-xs text-zinc-500 mb-2">Profil Durumu</p>
              <span className={profile.approved
                ? 'inline-block px-3 py-1 rounded-full bg-zinc-900 text-white text-sm font-semibold'
                : 'inline-block px-3 py-1 rounded-full bg-[#1E3A8A] text-white text-sm font-semibold'
              } data-testid="dashboard-approval-status">
                {profile.approved ? '✓ Onaylı' : '⏳ Onay Bekleniyor'}
              </span>
            </div>
            <div className="bg-white border-2 border-zinc-200 rounded-2xl p-5">
              <p className="text-xs text-zinc-500 mb-2">{user.role === 'venue' ? 'Kapasite' : 'Beklenen Katılımcı'}</p>
              <p className="text-3xl font-bold">
                {user.role === 'venue' ? capacityText(profile.min_capacity, profile.max_capacity, '') : profile.expected_attendees}
              </p>
            </div>
            <div className="bg-white border-2 border-zinc-200 rounded-2xl p-5">
              <p className="text-xs text-zinc-500 mb-2">Onaylı Eşleşmeler</p>
              <p className="text-3xl font-bold text-[#1E3A8A]">{matches.length}</p>
            </div>
          </div>

          {/* Pending notice */}
          {!profile.approved && (
            <div className="bg-[#1E3A8A]/5 border-2 border-[#1E3A8A]/30 rounded-2xl p-6">
              <div className="text-3xl mb-2">⏳</div>
              <h3 className="text-lg font-bold mb-2">Profilin Admin'in Onayını Bekliyor</h3>
              <p className="text-sm text-zinc-700">
                Onaylandıktan sonra eşleşme moduna geçip {user.role === 'venue' ? 'organizatörlerle' : 'mekânlarla'} doğrudan tanışabilirsin.
                Endişelenme — bir gün içinde sonuçlanır.
              </p>
            </div>
          )}

          {/* Hero CTA for approved users */}
          {profile.approved && user.role === 'organizer' && !activeRequest && (
            <Link
              to="/organizer/event-requests"
              className="block bg-[#1E3A8A] hover:bg-[#16307A] text-white rounded-2xl p-6 shadow-md transition-all hover:shadow-lg"
              data-testid="dashboard-new-event-request"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-2xl mb-1">🎯</div>
                  <h3 className="text-xl font-bold">Hangi etkinlik için eşleşmek istiyorsun?</h3>
                  <p className="text-sm opacity-90 mt-1">
                    Bu seferki etkinliğinin detaylarını gir; eşleşme önerileri buna göre hesaplansın.
                  </p>
                </div>
                <div className="text-3xl">→</div>
              </div>
            </Link>
          )}

          {profile.approved && user.role === 'organizer' && activeRequest && (
            <div className="space-y-3">
              <div className="bg-white border-2 border-[#1E3A8A] rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-xs text-[#9A4F00] font-bold mb-1">AKTİF ETKİNLİK TALEBİ</p>
                    <p className="text-lg font-bold">{activeRequest.title || 'Etkinlik'}</p>
                    <p className="text-sm text-zinc-600 mt-1">
                      {activeRequest.event_type} • {activeRequest.expected_attendees} kişi
                      {activeRequest.preferred_districts?.length > 0 && ` • ${activeRequest.preferred_districts.slice(0, 3).join(', ')}`}
                    </p>
                  </div>
                  <Link to="/organizer/event-requests" className="text-sm text-[#1E3A8A] font-semibold hover:underline">
                    Değiştir / Yenisini ekle →
                  </Link>
                </div>
              </div>
              <Link
                to="/swipe"
                className="block bg-[#1E3A8A] hover:bg-[#16307A] text-white rounded-2xl p-6 shadow-md transition-all hover:shadow-lg"
                data-testid="dashboard-start-swipe-button"
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-2xl mb-1">🔥</div>
                    <h3 className="text-xl font-bold">Eşleşme moduna geç</h3>
                    <p className="text-sm opacity-90 mt-1">
                      Bu talebine uygun mekânları tek tek incele, beğen ve eşleş.
                    </p>
                  </div>
                  <div className="text-3xl">→</div>
                </div>
              </Link>
            </div>
          )}

          {profile.approved && user.role === 'venue' && (
            <Link
              to="/swipe"
              className="block bg-[#1E3A8A] hover:bg-[#16307A] text-white rounded-2xl p-6 shadow-md transition-all hover:shadow-lg"
              data-testid="dashboard-start-swipe-button"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-2xl mb-1">🔥</div>
                  <h3 className="text-xl font-bold">Eşleşme moduna geç</h3>
                  <p className="text-sm opacity-90 mt-1">
                    Sana uygun organizatörleri tek tek incele, beğen ve eşleş.
                  </p>
                </div>
                <div className="text-3xl">→</div>
              </div>
            </Link>
          )}

          {/* Matches summary */}
          {profile.approved && matches.length > 0 && (
            <div>
              <h2 className="text-xl font-bold mb-4">🎉 Senin için bulduklarımız</h2>
              <div className="space-y-3">
                {matches.map((m) => (
                  <Link
                    key={m.id}
                    to={matchesPath}
                    className="block bg-white border-2 border-zinc-200 hover:border-[#1E3A8A] rounded-2xl p-5 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-500 mb-1">{user.role === 'venue' ? 'Organizatör' : 'Mekân'}</p>
                        <p className="font-bold text-lg truncate">{user.role === 'venue' ? m.organizer_name : m.venue_name}</p>
                      </div>
                      {/* A7: numerik uyum yüzdesi gösterilmez; ayrıntılar Eşleşmelerim'de gerekçe satırlarıyla. */}
                      {m.reasons_positive?.[0] && <p className="hidden sm:block text-xs text-zinc-600 max-w-[45%] truncate">✓ {m.reasons_positive[0]}</p>}
                      <div className="text-zinc-400 text-xl">→</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {profile.approved && matches.length === 0 && (
            <div className="bg-zinc-50 border-2 border-zinc-200 rounded-2xl p-8 text-center">
              <div className="text-3xl mb-2">🔍</div>
              <h3 className="text-lg font-bold mb-1">Henüz eşleşme yok</h3>
              <p className="text-sm text-zinc-600">
                Admin yeni eşleşmeleri hesapladığında burada görünecek.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, highlight, testId }) => (
  <div className="bg-white border-2 border-zinc-200 rounded-2xl p-5" data-testid={testId}>
    <p className="text-xs text-zinc-500 mb-2">{label}</p>
    <p className={`text-3xl font-bold ${highlight ? 'text-[#1E3A8A]' : 'text-zinc-900'}`}>{value}</p>
  </div>
);

const ActionCard = ({ emoji, title, description, to, testId }) => (
  <Link
    to={to}
    className="block bg-white border-2 border-zinc-200 hover:border-[#1E3A8A] rounded-2xl p-6 transition-colors"
    data-testid={testId}
  >
    <div className="text-3xl mb-3">{emoji}</div>
    <h3 className="text-lg font-bold mb-1">{title}</h3>
    <p className="text-sm text-zinc-600 mb-4">{description}</p>
    <span className="inline-flex items-center text-sm text-[#1E3A8A] font-semibold">İncele →</span>
  </Link>
);

export default Dashboard;

